import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { courseAPI } from '../services/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const token = localStorage.getItem('student_token');
  const user = localStorage.getItem('student_user');

  useEffect(() => {
    const validateToken = async () => {
  if (!token || !user) {
        setIsValid(false);
        setIsValidating(false);
        return;
      }

      try {
        // Try to make a lightweight API call to validate the token
        // This will trigger the interceptor if token is invalid
        await courseAPI.getCourses();
        setIsValid(true);
      } catch (error: any) {
        // If 401 or 403, token is invalid - interceptor already cleared storage
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Double-check storage is cleared (interceptor should have done this)
          if (localStorage.getItem('student_token')) {
            localStorage.removeItem('student_token');
            localStorage.removeItem('student_user');
          }
          setIsValid(false);
        } else {
          // Other errors might be network issues, but token might still be valid
          // Allow access but the error will be handled by the component
          setIsValid(true);
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, user]);

  if (isValidating) {
    // Show loading state while validating
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
        boxSizing: 'border-box',
      }}>
        <CircularProgress color="primary" />
      </div>
    );
  }

  if (!isValid || !token || !user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
